import React, { useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { PaymentInterface, SellerInterface } from "../../../../Models/Seller";
import { AcceptPaymentMethodEnum } from "../../../../enums/AcceptPaymentMethodEnum";
import { SellerAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/SellerAPIhandler";
import GGButton from "../../../CommonComponents/GGButton/GGButton";
import GGModalWindow from "../../../UtilityComponents/GGModalWindow/GGModalWindow";
import GGLoaderView from "../../../UtilityComponents/GGLoaderView/GGLoaderView";
import SnackBar, { SnackTypes } from "../../../UtilityComponents/SnackBar/SnackBar";
import GGDialogueBox, { dialogueType } from "../../../UtilityComponents/GGDialogueBox/GGDialogueBox";
import PaymentSetupPageMainContainer from "../../PaymentSetupComponents/PaymentSetupPageMainContainer";
import { SellerPaymentFormData } from "../../../../pages/AddNewGigPage/PaymentSetupPage/PaymentSetupPage";
import SellerPaymentMethodItem from "./SellerPaymentMethodItem";
import "./PaymentSettingsSubPanel.scss";
import { ErrorLogger } from "../../../../utils/Logger";

export const PaymentSettingsSubPanel = () => {
    const [seller, setSeller] = useState<SellerInterface | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [openAddPaymentMethodModal, setOpenAddPaymentMethodModal] = useState(false);
    const [openEditPaymentMethodModal, setOpenEditPaymentMethodModal] = useState(false);
    const [editingPaymentIndex, setEditingPaymentIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [showDefaultConfirmation, setShowDefaultConfirmation] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [selectedPaymentIndex, setSelectedPaymentIndex] = useState<number | null>(null);
    const snackBarRef = useRef<{ showSnackbar(): any }>(null);
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error,
    });

    useEffect(() => {
        const fetchSeller = async () => {
            setIsLoading(true);
            try {
                const sellerData = await SellerAPIHandler.shared.getSellerByAuthId();
                setSeller(sellerData);
            } catch (err) {
                ErrorLogger.log(err, 'PaymentSettingsSubPanel.fetchSeller');
                showSnackbar("Failed to load payment settings", SnackTypes.error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSeller();
    }, []);

    const showSnackbar = (message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state });
        snackBarRef.current?.showSnackbar();
    };

    const getPaymentMethodDisplayText = (paymentMethod: PaymentInterface): string => {
        switch (paymentMethod.paymentMethod) {
            case AcceptPaymentMethodEnum.UPI:
                return `UPI ID: ${JSON.parse(paymentMethod.paymentDetails)}`;
            case AcceptPaymentMethodEnum.BankAccount:
                const bankDetails = JSON.parse(paymentMethod.paymentDetails);
                return `Bank Account: ${bankDetails.accountNumber} (${bankDetails.bankName})`;
            default:
                return "Unknown payment method";
        }
    };

    const getPaymentInterfaceFromFormData = (paymentFormData: SellerPaymentFormData): PaymentInterface => {
        let paymentDetails: string;
        switch (paymentFormData.selectedPaymentMode) {
            case AcceptPaymentMethodEnum.UPI:
                paymentDetails = JSON.stringify(paymentFormData.upiId);
                break;
            case AcceptPaymentMethodEnum.BankAccount:
                paymentDetails = JSON.stringify(paymentFormData.bankAccountDetails);
                break;
            default:
                paymentDetails = "";
        }

        return {
            paymentMethod: paymentFormData.selectedPaymentMode,
            paymentDetails: paymentDetails,
        };
    };

    const getFormDataFromPaymentInterface = (paymentMethod: PaymentInterface): SellerPaymentFormData => {
        let formData: SellerPaymentFormData = {
            selectedPaymentMode: paymentMethod.paymentMethod as AcceptPaymentMethodEnum,
            upiId: "",
            bankAccountDetails: {
                accountNumber: "",
                bankIFSC: "",
                holderName: "",
                bankName: "",
            },
            acknowledgementChecked: false,
        };

        switch (paymentMethod.paymentMethod) {
            case AcceptPaymentMethodEnum.UPI:
                formData.upiId = JSON.parse(paymentMethod.paymentDetails);
                break;
            case AcceptPaymentMethodEnum.BankAccount:
                formData.bankAccountDetails = JSON.parse(paymentMethod.paymentDetails);
                break;
        }

        return formData;
    };

    const deletePaymentMethod = (index: number) => {
        const paymentMethod = seller?.paymentDetails && seller.paymentDetails[index];

        if (!paymentMethod) {
            showSnackbar("Selected payment method not found", SnackTypes.error);
            return;
        }

        setSelectedPaymentIndex(index);
        setShowDeleteConfirmation(true);
    };

    const confirmDelete = () => {
        if (selectedPaymentIndex === null || !seller) return;

        const sellerPaymentDetails = [...(seller.paymentDetails ?? [])];
        sellerPaymentDetails.splice(selectedPaymentIndex, 1);
        updateSellerPaymentMethods(sellerPaymentDetails, "Payment method deleted successfully");
        setShowDeleteConfirmation(false);
        setSelectedPaymentIndex(null);
    };

    const cancelDelete = () => {
        setShowDeleteConfirmation(false);
        setSelectedPaymentIndex(null);
    };

    const addPaymentMethod = (paymentFormData: SellerPaymentFormData) => {
        if (!seller) return;

        let paymentDetailsToAdd: PaymentInterface = getPaymentInterfaceFromFormData(paymentFormData);
        paymentDetailsToAdd.isDefault = true;

        let existingPaymentDetails = seller.paymentDetails ?? [];
        existingPaymentDetails = existingPaymentDetails.map((paymentItem) => {
            return { ...paymentItem, isDefault: false };
        });

        let paymentDetails = [...existingPaymentDetails, paymentDetailsToAdd];

        updateSellerPaymentMethods(paymentDetails, "Payment method added and set as default successfully!");
    };

    const editPaymentMethod = (paymentFormData: SellerPaymentFormData) => {
        if (editingPaymentIndex === null || !seller) {
            showSnackbar("No payment method selected for editing", SnackTypes.error);
            return;
        }

        setEditLoading(true);
        setOpenEditPaymentMethodModal(false);

        let updatedPaymentMethod: PaymentInterface = getPaymentInterfaceFromFormData(paymentFormData);

        const originalPaymentMethod = seller.paymentDetails?.[editingPaymentIndex];
        if (originalPaymentMethod) {
            updatedPaymentMethod.isDefault = originalPaymentMethod.isDefault;
        }

        let updatedPaymentDetails = [...(seller.paymentDetails ?? [])];
        updatedPaymentDetails[editingPaymentIndex] = updatedPaymentMethod;

        updateSellerPaymentMethods(updatedPaymentDetails, "Payment method updated successfully!");
        setEditingPaymentIndex(null);
    };

    const openEditModal = (index: number) => {
        setEditingPaymentIndex(index);
        setOpenEditPaymentMethodModal(true);
    };

    const setDefaultPaymentMethod = (index: number) => {
        const paymentMethod = seller?.paymentDetails && seller.paymentDetails[index];

        if (!paymentMethod) {
            showSnackbar("Selected payment method not found", SnackTypes.error);
            return;
        }

        if (paymentMethod.isDefault) {
            return;
        }

        setSelectedPaymentIndex(index);
        setShowDefaultConfirmation(true);
    };

    const confirmSetDefault = () => {
        if (selectedPaymentIndex === null || !seller) return;

        let updatedPaymentDetails = [...(seller.paymentDetails ?? [])];

        updatedPaymentDetails = updatedPaymentDetails.map((paymentItem) => {
            return { ...paymentItem, isDefault: false };
        });

        updatedPaymentDetails[selectedPaymentIndex] = {
            ...updatedPaymentDetails[selectedPaymentIndex],
            isDefault: true,
        };

        updateSellerPaymentMethods(updatedPaymentDetails, "Default payment method updated successfully!");
        setShowDefaultConfirmation(false);
        setSelectedPaymentIndex(null);
    };

    const cancelSetDefault = () => {
        setShowDefaultConfirmation(false);
        setSelectedPaymentIndex(null);
    };

    const updateSellerPaymentMethods = (paymentDetails: PaymentInterface[], successMessage: string) => {
        if (!seller) return;

        setLoading(true);
        setOpenAddPaymentMethodModal(false);
        SellerAPIHandler.shared
            .updateSeller({ paymentDetails })
            .then((updatedSeller) => {
                setSeller(updatedSeller);
                showSnackbar(successMessage, SnackTypes.success);
            })
            .catch((err) => {
                ErrorLogger.log(err, 'PaymentSettingsSubPanel.updateSellerPaymentMethods');
                showSnackbar(err as string, SnackTypes.error);
            })
            .finally(() => {
                setLoading(false);
                setEditLoading(false);
            });
    };

    if (isLoading) {
        return (
            <section className="payment-settings-subpanel">
                <Box className="payment-settings-content">
                    <GGLoaderView />
                </Box>
            </section>
        );
    }

    if (!seller) {
        return (
            <section className="payment-settings-subpanel">
                <Box className="payment-settings-content">
                    <Typography variant="h6" sx={{ mb: 2, color: "error.main" }}>
                        Unable to load payment settings. Please try again later.
                    </Typography>
                </Box>
            </section>
        );
    }

    return (
        <section className="payment-settings-subpanel">
            {loading && <GGLoaderView />}
            <SnackBar
                ref={snackBarRef}
                severity={snackBarParams.state}
                alertDuration={5000}
                message={snackBarParams.message}
            />

            <Box className="payment-settings-content">
                <div className="payment-settings-header">
                    <Typography variant="h5" className="payment-settings-title">
                        Payment Settings
                    </Typography>
                    <Typography variant="body2" className="payment-settings-description">
                        Manage your payment methods for receiving money. Set a default payment method that will be used for all transactions.
                    </Typography>
                </div>

                <div className="payment-settings-actions">
                    <GGButton
                        variant="outlined"
                        startIcon={<AddOutlinedIcon />}
                        onClick={() => setOpenAddPaymentMethodModal(true)}
                        disableClickShrink
                    >
                        <Typography fontSize={14} fontWeight={600}>
                            Add Payment Method
                        </Typography>
                    </GGButton>
                </div>

                <div className="payment-methods-container">
                    {seller.paymentDetails && seller.paymentDetails.length > 0 ? (
                        <div className="payment-methods-list">
                            {seller.paymentDetails.map((paymentMethod, index) => (
                                <SellerPaymentMethodItem
                                    key={index}
                                    paymentMethod={paymentMethod}
                                    deleteItem={() => deletePaymentMethod(index)}
                                    editItem={() => openEditModal(index)}
                                    setAsDefault={() => setDefaultPaymentMethod(index)}
                                    editLoading={editLoading}
                                />
                            ))}
                        </div>
                    ) : (
                        <Box className="no-payment-methods">
                            <Typography variant="body1" sx={{ color: "#666", textAlign: "center" }}>
                                No payment methods added yet. Click "Add Payment Method" to get started.
                            </Typography>
                        </Box>
                    )}
                </div>
            </Box>

            <GGModalWindow
                childView={
                    <PaymentSetupPageMainContainer
                        savedFormData={{
                            selectedPaymentMode: AcceptPaymentMethodEnum.UPI,
                            upiId: "",
                            bankAccountDetails: {
                                accountNumber: "",
                                bankIFSC: "",
                                holderName: "",
                                bankName: "",
                            },
                            acknowledgementChecked: false,
                        }}
                        uponValidatedSubmission={addPaymentMethod}
                    />
                }
                open={openAddPaymentMethodModal}
                onCloseHandler={() => setOpenAddPaymentMethodModal(false)}
                title="Add New Payment Method"
                width="70%"
            />

            <GGModalWindow
                childView={
                    <PaymentSetupPageMainContainer
                        savedFormData={
                            editingPaymentIndex !== null && seller.paymentDetails?.[editingPaymentIndex]
                                ? getFormDataFromPaymentInterface(seller.paymentDetails[editingPaymentIndex])
                                : {
                                      selectedPaymentMode: AcceptPaymentMethodEnum.UPI,
                                      upiId: "",
                                      bankAccountDetails: {
                                          accountNumber: "",
                                          bankIFSC: "",
                                          holderName: "",
                                          bankName: "",
                                      },
                                      acknowledgementChecked: false,
                                  }
                        }
                        uponValidatedSubmission={editPaymentMethod}
                    />
                }
                open={openEditPaymentMethodModal}
                onCloseHandler={() => {
                    setOpenEditPaymentMethodModal(false);
                    setEditingPaymentIndex(null);
                }}
                title="Edit Payment Method"
                width="70%"
            />

            <GGDialogueBox
                type={dialogueType.Info}
                isOpen={showDefaultConfirmation}
                message={
                    selectedPaymentIndex !== null && seller.paymentDetails?.[selectedPaymentIndex]
                        ? `Are you sure you want to set "${getPaymentMethodDisplayText(
                              seller.paymentDetails[selectedPaymentIndex]
                          )}" as your default payment method? This will replace your current default payment method.`
                        : "Are you sure you want to set this payment method as your default? This will replace your current default payment method."
                }
                closeHandler={cancelSetDefault}
                successHandler={confirmSetDefault}
            />

            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={showDeleteConfirmation}
                message={
                    selectedPaymentIndex !== null && seller.paymentDetails?.[selectedPaymentIndex]
                        ? `Are you sure you want to delete "${getPaymentMethodDisplayText(
                              seller.paymentDetails[selectedPaymentIndex]
                          )}"? This action cannot be undone.`
                        : "Are you sure you want to delete this payment method? This action cannot be undone."
                }
                closeHandler={cancelDelete}
                successHandler={confirmDelete}
            />
        </section>
    );
};

